/**
 * trading.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin trading management — view and manage users' open and closed trades.
 * Route: /admin/trading
 *
 * Tables used:
 *   open_positions     (id, user_id → users, broker_conn_id → broker_connections,
 *                       symbol, direction, volume, open_price, current_price,
 *                       close_price, profit_loss, status, opened_at, closed_at,
 *                       approval_status  — 'pending' | 'approved' | 'declined'
 *                       (default 'pending'; only settled to wallet when approved))
 *   broker_connections (id, user_id → users, broker_name)
 *   users              (id, first_name, last_name, email)
 *   wallets            (user_id, currency, balance)
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  // sidebar
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut, Users,CreditCard, MapPin,
  // page
  Search, RefreshCw, Eye, X, Plus, TrendingUp, TrendingDown,
  AlertCircle, CheckCircle2, XCircle, Clock,
  ChevronUp, ChevronDown, Activity, Briefcase, Menu,
} from "lucide-react";

const PER_PAGE = 15;

// ─── Sidebar nav ──────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users   },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet  },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
   {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
    ],
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --border-glow:  rgba(99,102,241,0.3);
    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);
    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;
    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
  }

  html, body, #root { height: 100%; background: var(--bg-base); font-family: var(--font-sans); color-scheme: dark; }

  /* Shell */
  .td-shell {
    display: flex; height: 100vh; height: 100dvh; overflow: hidden;
    background: var(--bg-base); position: relative;
  }
  .td-shell::before {
    content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.018;
  }

  /* ── Sidebar ── */
  .ad-sidebar {
    width: var(--sidebar-w); flex-shrink: 0;
    display: flex; flex-direction: column; height: 100%; overflow: hidden;
    background: var(--bg-surface); border-right: 1px solid var(--border);
    position: relative; z-index: 50;
    transition: transform .3s cubic-bezier(.4,0,.2,1); will-change: transform;
  }
  .ad-sidebar::after {
    content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events: none;
  }
  .ad-logo {
    display: flex; align-items: center; gap: 11px;
    padding: 0 18px; height: var(--topbar-h);
    border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .ad-logo-icon {
    width: 32px; height: 32px; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 0 0 0 1px rgba(99,102,241,0.4), 0 4px 16px rgba(99,102,241,0.35), 0 0 32px rgba(99,102,241,0.15);
  }
  .ad-logo-text strong {
    font-size: 13.5px; font-weight: 800; letter-spacing: -0.4px;
    background: linear-gradient(to right, #eeeeff, #a5b4fc);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .ad-logo-text span { font-size: 9.5px; color: var(--text-muted); display: block; margin-top: 3px; letter-spacing: 0.8px; text-transform: uppercase; font-weight: 500; }
  .ad-sidebar-close { display: none; margin-left: auto; background: none; border: none; border-radius: 7px; width: 28px; height: 28px; align-items: center; justify-content: center; color: var(--text-muted); cursor: pointer; flex-shrink: 0; transition: all .15s; }
  .ad-sidebar-close:hover { background: var(--rose-dim); color: var(--rose); }
  .ad-sidebar-overlay { display: none; position: fixed; inset: 0; z-index: 40; background: rgba(0,0,0,.6); backdrop-filter: blur(2px); }
  .td-menu-btn { display: none; background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-sm); width: 36px; height: 36px; align-items: center; justify-content: center; color: var(--text-secondary); cursor: pointer; flex-shrink: 0; transition: all .15s; }
  .td-menu-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,.25); }
  .ad-nav { flex: 1; min-height: 0; overflow-y: auto; padding: 18px 10px; display: flex; flex-direction: column; gap: 22px; -webkit-overflow-scrolling: touch; scroll-behavior: smooth; }
  .ad-nav::-webkit-scrollbar { width: 3px; }
  .ad-nav::-webkit-scrollbar-track { background: transparent; }
  .ad-nav::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.18); border-radius: 99px; }
  .ad-nav-section-label { font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 0 10px; margin-bottom: 5px; }
  .ad-nav-items { display: flex; flex-direction: column; gap: 1px; }
  .ad-nav-link {
    display: flex; align-items: center; gap: 9px; padding: 8px 10px; border-radius: var(--radius-sm);
    font-size: 12.5px; font-weight: 500; color: var(--text-secondary);
    text-decoration: none; transition: all 0.18s ease; position: relative;
  }
  .ad-nav-link:hover { background: var(--bg-hover); color: var(--text-primary); }
  .ad-nav-link.active {
    background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%);
    color: var(--accent-light); font-weight: 600;
    box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15);
  }
  .ad-nav-link.active::before {
    content: ''; position: absolute; left: 0; top: 18%; bottom: 18%;
    width: 2.5px; border-radius: 99px;
    background: linear-gradient(to bottom, var(--accent), var(--purple));
    box-shadow: 0 0 10px var(--accent-glow), 0 0 20px rgba(99,102,241,0.2);
  }
  .ad-nav-link svg { flex-shrink: 0; opacity: 0.65; transition: opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity: 1; }
  .ad-nav-chevron { margin-left: auto; opacity: 0.35; }
  .ad-user-card { flex-shrink: 0; border-top: 1px solid var(--border); padding: 12px 10px; background: linear-gradient(to top, rgba(99,102,241,0.04) 0%, transparent 100%); }
  .ad-user-inner { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: var(--radius-sm); transition: background 0.15s; }
  .ad-user-inner:hover { background: var(--bg-hover); }
  .ad-user-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-dim), rgba(139,92,246,0.2));
    border: 1.5px solid rgba(99,102,241,0.35);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 700; color: var(--accent-light); flex-shrink: 0;
  }
  .ad-user-info { flex: 1; min-width: 0; }
  .ad-user-info strong { font-size: 12px; font-weight: 600; color: var(--text-primary); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ad-user-info span   { font-size: 10px; color: var(--text-muted); display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .ad-logout-btn {
    width: 28px; height: 28px; border-radius: 7px; background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center; color: var(--text-muted); transition: all 0.15s;
  }
  .ad-logout-btn:hover { background: var(--rose-dim); color: var(--rose); }

  /* ── Page column ── */
  .td-page { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; z-index: 1; min-width: 0; }

  /* Topbar */
  .td-topbar {
    flex-shrink: 0; height: var(--topbar-h);
    background: rgba(10,10,22,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px; position: relative;
  }
  .td-topbar::after {
    content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 0%, var(--accent-glow) 50%, transparent 100%);
  }
  .td-topbar-left  { display: flex; align-items: center; gap: 14px; }
  .td-topbar-right { display: flex; align-items: center; gap: 8px; }
  .td-topbar-badge {
    display: flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 99px;
    background: var(--accent-dim); border: 1px solid rgba(99,102,241,0.2);
    font-size: 10px; font-weight: 700; color: var(--accent-light); letter-spacing: 0.5px; text-transform: uppercase;
  }
  .td-topbar-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.4px; }
  .td-topbar-sub   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }

  /* Buttons */
  .td-btn {
    display: flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: var(--radius-sm);
    font-size: 11.5px; font-weight: 500; cursor: pointer; transition: all 0.18s; font-family: var(--font-sans);
    border: 1px solid var(--border-hi); background: var(--bg-card); color: var(--text-secondary);
  }
  .td-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Main */
  .td-main {
    flex: 1; overflow-y: auto; overflow-x: hidden; padding: 26px 28px 48px;
    display: flex; flex-direction: column; gap: 20px;
    -webkit-overflow-scrolling: touch; scroll-behavior: smooth; overscroll-behavior-y: contain;
  }
  .td-main::-webkit-scrollbar { width: 4px; }
  .td-main::-webkit-scrollbar-track { background: transparent; }
  .td-main::-webkit-scrollbar-thumb { background: rgba(99,102,241,0.25); border-radius: 99px; }

  /* Stat grid */
  .td-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
  .td-stat {
    background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 18px 20px;
    display: flex; flex-direction: column; gap: 12px; position: relative; overflow: hidden;
    transition: border-color 0.22s, box-shadow 0.22s, transform 0.18s;
  }
  .td-stat::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse at 110% -10%, var(--stat-glow, transparent) 0%, transparent 60%);
    pointer-events: none;
  }
  .td-stat::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(to right, transparent 20%, var(--stat-bar, transparent) 50%, transparent 80%);
    pointer-events: none;
  }
  .td-stat:hover { border-color: rgba(255,255,255,0.1); box-shadow: 0 4px 24px rgba(0,0,0,0.4); transform: translateY(-1px); }
  .td-stat-top   { display: flex; align-items: flex-start; justify-content: space-between; }
  .td-stat-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; color: var(--text-muted); }
  .td-stat-icon  { width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .td-stat-value { font-size: 26px; font-weight: 700; color: var(--text-primary); letter-spacing: -1.5px; font-family: var(--font-mono); line-height: 1; }
  .td-stat-sub   { font-size: 10.5px; color: var(--text-muted); margin-top: 4px; }

  /* Tabs */
  .td-tabs { display: flex; gap: 2px; padding: 4px; background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); width: fit-content; }
  .td-tab {
    display: flex; align-items: center; gap: 7px; padding: 7px 16px; border-radius: 10px;
    font-size: 12.5px; font-weight: 500; cursor: pointer; border: none; background: none; transition: all 0.18s; font-family: var(--font-sans);
    color: var(--text-muted);
  }
  .td-tab:hover { color: var(--text-secondary); }
  .td-tab.active { background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%); color: var(--accent-light); box-shadow: inset 0 0 0 1px rgba(99,102,241,0.15); }
  .td-tab-badge {
    min-width: 18px; height: 18px; padding: 0 5px; border-radius: 99px;
    font-size: 9.5px; font-weight: 700; display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.07); color: var(--text-muted);
  }
  .td-tab-badge.live { background: var(--amber); color: #000; }

  /* Table card */
  .td-table-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .td-table-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-bottom: 1px solid var(--border); gap: 12px; }
  .td-table-title { font-size: 13px; font-weight: 600; color: var(--text-primary); }
  .td-table-count { font-size: 10px; color: var(--text-muted); background: rgba(255,255,255,0.05); border: 1px solid var(--border); border-radius: 99px; padding: 2px 8px; }
  .td-search-wrap { position: relative; }
  .td-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  .td-search {
    padding: 7px 12px 7px 30px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 12px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; width: 200px; transition: border-color 0.18s;
  }
  .td-search::placeholder { color: var(--text-muted); }
  .td-search:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }

  /* Table */
  .td-table { width: 100%; border-collapse: collapse; }
  .td-th {
    padding: 10px 16px; text-align: left; font-size: 9.5px; font-weight: 700;
    letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted);
    border-bottom: 1px solid var(--border); cursor: pointer; white-space: nowrap; user-select: none;
    transition: color 0.15s;
  }
  .td-th:hover { color: var(--text-secondary); }
  .td-th.right { text-align: right; }
  .td-td { padding: 13px 16px; border-bottom: 1px solid rgba(255,255,255,0.03); vertical-align: middle; }
  .td-tr { transition: background 0.15s; }
  .td-tr:hover { background: rgba(255,255,255,0.018); }
  .td-tr:last-child .td-td { border-bottom: none; }

  /* Avatar */
  .td-avatar {
    width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700;
  }
  .td-avatar-indigo { background: rgba(99,102,241,0.18); color: #818cf8; }
  .td-avatar-green  { background: rgba(52,211,153,0.18); color: #34d399; }
  .td-avatar-amber  { background: rgba(251,191,36,0.18);  color: #fbbf24; }
  .td-avatar-rose   { background: rgba(251,113,133,0.18); color: #fb7185; }
  .td-avatar-purple { background: rgba(167,139,250,0.18); color: #a78bfa; }

  /* Chips */
  .td-chip {
    display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px;
    border-radius: 99px; font-size: 10px; font-weight: 600;
  }
  .td-chip-open   { background: var(--amber-dim); color: var(--amber); border: 1px solid rgba(251,191,36,0.25); }
  .td-chip-closed { background: rgba(255,255,255,0.05); color: var(--text-muted); border: 1px solid var(--border); }
  .td-chip-buy    { background: var(--green-dim);  color: var(--green); border: 1px solid rgba(52,211,153,0.25); }
  .td-chip-sell   { background: var(--rose-dim);   color: var(--rose);  border: 1px solid rgba(251,113,133,0.25); }
  .td-chip-pending  { background: var(--amber-dim); color: var(--amber); border: 1px solid rgba(251,191,36,0.25); }
  .td-chip-approved { background: var(--green-dim); color: var(--green); border: 1px solid rgba(52,211,153,0.25); }
  .td-chip-declined { background: var(--rose-dim);  color: var(--rose);  border: 1px solid rgba(251,113,133,0.25); }

  /* Action btn */
  .td-icon-btn {
    width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--border);
    background: none; cursor: pointer; display: flex; align-items: center; justify-content: center;
    color: var(--text-muted); transition: all 0.15s;
  }
  .td-icon-btn:hover { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Pagination */
  .td-pagination { display: flex; align-items: center; justify-content: space-between; padding: 12px 20px; border-top: 1px solid var(--border); }
  .td-page-info  { font-size: 11px; color: var(--text-muted); }
  .td-page-btns  { display: flex; gap: 3px; }
  .td-page-btn   {
    width: 28px; height: 28px; border-radius: 6px; border: 1px solid var(--border);
    background: none; cursor: pointer; font-size: 11px; color: var(--text-muted); font-family: var(--font-sans);
    display: flex; align-items: center; justify-content: center; transition: all 0.15s;
  }
  .td-page-btn:hover { background: var(--bg-hover); color: var(--text-primary); }
  .td-page-btn.active { background: var(--accent-dim); color: var(--accent-light); border-color: rgba(99,102,241,0.3); }

  /* Empty state */
  .td-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 52px 20px; gap: 10px; color: var(--text-muted); }
  .td-empty-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,0.04); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; }

  /* Skeleton */
  .td-skel { background: rgba(255,255,255,0.05); border-radius: 4px; animation: td-pulse 1.4s ease-in-out infinite; }
  @keyframes td-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

  /* Modal */
  .td-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 20px; overflow-y: auto; }
  .td-modal {
    background: var(--bg-elevated); border: 1px solid var(--border-hi);
    border-radius: var(--radius-lg); width: 100%; max-width: 480px;
    max-height: calc(100vh - 40px); max-height: calc(100dvh - 40px);
    display: flex; flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6); overflow: hidden;
  }
  .td-modal-head { display: flex; align-items: flex-start; justify-content: space-between; padding: 20px 24px 18px; border-bottom: 1px solid var(--border); gap: 12px; flex-shrink: 0; }
  .td-modal-title { font-size: 15px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.3px; }
  .td-modal-sub   { font-size: 12px; color: var(--text-secondary); margin-top: 3px; }
  .td-modal-body  {
    padding: 22px 24px; display: flex; flex-direction: column; gap: 16px;
    overflow-y: auto; flex: 1; min-height: 0;
    -webkit-overflow-scrolling: touch; scroll-behavior: smooth; overscroll-behavior-y: contain;
  }
  .td-modal-foot  { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 16px 24px; border-top: 1px solid var(--border); flex-shrink: 0; }
  .td-close-btn {
    width: 28px; height: 28px; border-radius: 7px; background: none; border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center; color: var(--text-muted); cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .td-close-btn:hover { background: var(--rose-dim); color: var(--rose); border-color: rgba(251,113,133,0.25); }
  .td-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .td-detail-cell { padding: 12px 14px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.025); border: 1px solid var(--border); }
  .td-detail-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
  .td-detail-val   { font-size: 13px; font-weight: 600; color: var(--text-primary); font-family: var(--font-mono); }
  .td-user-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: var(--radius-sm); background: rgba(255,255,255,0.025); border: 1px solid var(--border); margin-bottom: 4px; }
  .td-section-label { font-size: 9.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; }
  .td-ghost-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: var(--radius-sm);
    background: none; border: 1px solid var(--border-hi); color: var(--text-secondary);
    font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.15s; font-family: var(--font-sans);
  }
  .td-ghost-btn:hover { background: var(--bg-hover); color: var(--text-primary); }


  /* Form elements */
  .td-form-row   { display: flex; flex-direction: column; gap: 5px; }
  .td-form-label { font-size: 10px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; color: var(--text-muted); }
  .td-form-input, .td-form-select {
    padding: 8px 12px; background: var(--bg-surface); border: 1px solid var(--border);
    border-radius: var(--radius-sm); font-size: 12.5px; color: var(--text-primary);
    font-family: var(--font-sans); outline: none; transition: border-color 0.18s; width: 100%;
  }
  .td-form-input::placeholder { color: var(--text-muted); }
  .td-form-input:focus, .td-form-select:focus { border-color: rgba(99,102,241,0.45); box-shadow: 0 0 0 3px rgba(99,102,241,0.08); }
  .td-form-select option { background: var(--bg-elevated); }
  .td-save-btn {
    display: flex; align-items: center; gap: 6px; padding: 7px 18px; border-radius: var(--radius-sm);
    background: linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%);
    border: none; color: #fff; font-size: 12px; font-weight: 600; cursor: pointer;
    font-family: var(--font-sans); transition: opacity 0.18s; box-shadow: 0 4px 14px rgba(99,102,241,0.35);
  }
  .td-save-btn:hover:not(:disabled) { opacity: 0.88; }
  .td-save-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .td-btn-accent {
    background: linear-gradient(135deg, rgba(99,102,241,0.22) 0%, rgba(139,92,246,0.15) 100%);
    color: var(--accent-light); border-color: rgba(99,102,241,0.3);
  }
  .td-btn-accent:hover { background: linear-gradient(135deg, rgba(99,102,241,0.32) 0%, rgba(139,92,246,0.22) 100%); }

  /* Gate */
  .td-gate { height: 100vh; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 14px; background: var(--bg-base); }
  .td-gate-icon { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg,#6366f1,#a78bfa); box-shadow: 0 0 24px rgba(99,102,241,0.4); display: flex; align-items: center; justify-content: center; }

  /* ── Responsive ── */
  @media (min-width: 1400px) { :root { --sidebar-w: 252px; } .td-main { padding: 30px 36px; } .td-topbar { padding: 0 36px; } }
  @media (max-width: 1280px) { .td-stat-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 1023px) { :root { --sidebar-w: 220px; } .td-main { padding: 20px; gap: 16px; } .td-topbar { padding: 0 16px; } }

  @media (max-width: 767px) {
    :root { --topbar-h: 56px; }
    .td-menu-btn { display: flex; }
    .ad-sidebar-close { display: flex; }
    .ad-sidebar-overlay { display: block; }
    .ad-sidebar {
      position: fixed; top: 0; left: 0; bottom: 0;
      height: 100%; height: 100dvh; overflow: hidden;
      transform: translateX(-100%); box-shadow: 4px 0 32px rgba(0,0,0,.6); z-index: 50;
    }
    .ad-sidebar.open { transform: translateX(0); }
    .td-topbar { padding: 0 14px; }
    .td-topbar-sub { display: none; }
    .td-topbar-badge { display: none; }
    .td-main { padding: 14px; gap: 14px; }
    .td-stat-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
    .td-table-head { flex-wrap: wrap; }
    .td-search-wrap { width: 100%; }
    .td-search { width: 100%; }
    .td-tabs { width: 100%; }
    .td-tab { flex: 1; justify-content: center; }
    .td-pagination { flex-direction: column; align-items: flex-start; gap: 8px; }
    .td-detail-grid { grid-template-columns: 1fr; }
    .td-overlay { padding: 0; align-items: flex-end; }
    .td-modal { max-width: 100%; border-radius: var(--radius-md); max-height: 92vh; max-height: 92dvh; }
  }

  @media (max-width: 479px) {
    .td-stat-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    .td-stat { padding: 14px 12px; }
    .td-stat-value { font-size: 21px; }
    .ad-sidebar { width: 80%; max-width: 280px; }
  }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDateTime(str) {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtCurrency(val) {
  if (val == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(val);
}
function fullName(user) {
  if (!user) return "Unknown";
  const first = user.first_name ?? "";
  const last  = user.last_name  ?? "";
  return `${first} ${last}`.trim() || user.email || "Unknown";
}
function initials(user) {
  const first = (user?.first_name ?? "").trim();
  const last  = (user?.last_name  ?? "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first)         return first.slice(0, 2).toUpperCase();
  return "?";
}
const PALETTE = ["indigo", "green", "amber", "rose", "purple"];
function avatarColor(id) {
  const n = typeof id === "number" ? id : (id ? id.charCodeAt(0) : 0);
  return PALETTE[n % PALETTE.length];
}

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end, onNavigate }) {
  return (
    <NavLink to={to} end={end} onClick={onNavigate} className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

function SortIcon({ field, sortField, sortDir }) {
  if (field !== sortField) return <ChevronUp size={9} style={{ opacity: 0.2, marginLeft: 4, display: "inline" }} />;
  return sortDir === 1
    ? <ChevronUp   size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />
    : <ChevronDown size={9} style={{ color: "var(--accent-light)", marginLeft: 4, display: "inline" }} />;
}

// ─── Position detail modal ────────────────────────────────────────────────────
// Adds `amount` (positive for profit, negative for loss) onto a user's
// wallet balance for the given currency. Creates the wallet row if the
// user doesn't have one yet for that currency. Shared by the add-trade
// and edit-trade flows so wallet reconciliation stays consistent.
async function applyWalletAdjustment(userId, currency, amount) {
  if (!amount) return;

  const { data: wallet, error: fetchErr } = await supabase
    .from("wallets")
    .select("id, balance")
    .eq("user_id", userId)
    .eq("currency", currency)
    .maybeSingle();

  if (fetchErr) throw fetchErr;

  if (wallet) {
    const newBalance = Number(wallet.balance) + amount;
    const { error: updErr } = await supabase
      .from("wallets")
      .update({ balance: newBalance, updated_at: new Date().toISOString() })
      .eq("id", wallet.id);
    if (updErr) throw updErr;
  } else {
    const { error: insErr } = await supabase
      .from("wallets")
      .insert({ user_id: userId, currency, balance: amount });
    if (insErr) throw insErr;
  }
}

// Converts a stored timestamp into the `YYYY-MM-DDTHH:mm` shape a
// <input type="datetime-local"> expects, in the browser's local time.
function toDateTimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function PositionModal({ position, onClose, onSaved }) {
  const user  = position.users;
  const isOpen = position.status === "open";
  const aColor = avatarColor(user?.id ?? "");

  // Admin can edit every field on a trade — including closed trades,
  // where correcting the close price / P&L after the fact is common.
  // Closed trades start as approval_status = 'pending' and only hit the
  // user's wallet balance once an admin sets approval_status = 'approved'.
  const [form, setForm] = useState({
    symbol:           position.symbol ?? "",
    trade_type:       position.trade_type ?? "buy",
    lots:             position.lots ?? "",
    open_price:       position.open_price ?? "",
    current_price:    position.current_price ?? "",
    close_price:      position.close_price ?? "",
    profit_loss:      position.profit_loss ?? 0,
    status:           position.status ?? "open",
    approval_status:  position.approval_status ?? "pending",
    opened_at:        toDateTimeLocal(position.opened_at),
    closed_at:        toDateTimeLocal(position.closed_at),
  });
  const [adjustWallet, setAdjustWallet] = useState(true);
  const [walletCurrency, setWalletCurrency] = useState("USD");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pnlNow  = parseFloat(form.profit_loss) || 0;
  const pnlPos  = pnlNow >= 0;
  const pnlChanged = pnlNow !== (position.profit_loss ?? 0);
  const prevApproval = position.approval_status ?? "pending";
  const isClosedForm = form.status === "closed";

  async function handleSave() {
    setErr("");
    if (!form.symbol)  return setErr("Symbol is required.");
    if (!form.lots)    return setErr("Lots / volume is required.");
    if (!form.open_price) return setErr("Open price is required.");
    setSaving(true);
    try {
      const payload = {
        symbol:           form.symbol.toUpperCase().trim(),
        trade_type:       form.trade_type,
        lots:             parseFloat(form.lots),
        open_price:       parseFloat(form.open_price),
        current_price:    form.current_price !== "" ? parseFloat(form.current_price) : null,
        close_price:      form.close_price   !== "" ? parseFloat(form.close_price)   : null,
        profit_loss:      parseFloat(form.profit_loss) || 0,
        status:           form.status,
        approval_status:  form.approval_status || "pending",
        opened_at:        form.opened_at ? new Date(form.opened_at).toISOString() : null,
        closed_at:        form.closed_at ? new Date(form.closed_at).toISOString() : null,
      };

      const { error: updErr } = await supabase
        .from("open_positions")
        .update(payload)
        .eq("id", position.id);
      if (updErr) throw updErr;

      // Wallet settlement rules for closed trades:
      //  • Only when approval_status becomes 'approved' do we credit/debit the P&L.
      //  • Revoking approval reverses the previously settled amount.
      //  • If already approved and the admin edits P&L, the optional checkbox
      //    applies the delta (same as before).
      if (user?.id) {
        const wasApproved = prevApproval === "approved";
        const isNowApproved = isClosedForm && payload.approval_status === "approved";
        const prevPnl = position.profit_loss ?? 0;
        const newPnl  = payload.profit_loss;

        if (isNowApproved && !wasApproved) {
          // First approval → settle the current P&L into the wallet
          await applyWalletAdjustment(user.id, walletCurrency, newPnl);
        } else if (wasApproved && !isNowApproved) {
          // Approval revoked → reverse the previously settled P&L
          await applyWalletAdjustment(user.id, walletCurrency, -prevPnl);
        } else if (wasApproved && isNowApproved && pnlChanged && adjustWallet) {
          // Already approved, P&L corrected → apply delta
          await applyWalletAdjustment(user.id, walletCurrency, newPnl - prevPnl);
        }
      }

      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message ?? "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="td-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="td-modal">
        <div className="td-modal-head">
          <div>
            <div className="td-modal-title">{position.symbol ?? "Position"} detail</div>
            <div className="td-modal-sub">Position ID: #{position.id}</div>
          </div>
          <button className="td-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="td-modal-body" style={{ gap: 12 }}>
          {err && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "var(--rose-dim)", border: "1px solid rgba(251,113,133,0.25)", fontSize: 12, color: "var(--rose)" }}>
              {err}
            </div>
          )}

          {/* User identity (read-only — reassigning a trade to another user isn't supported here) */}
          <div className="td-user-row">
            <div className={`td-avatar td-avatar-${aColor}`}>
              {initials(user)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{fullName(user)}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{user?.email}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              <span className={`td-chip ${isOpen ? "td-chip-open" : "td-chip-closed"}`}>
                {isOpen ? <Activity size={9} /> : <CheckCircle2 size={9} />}
                {position.status}
              </span>
              {!isOpen && (
                <span className={`td-chip ${
                  (position.approval_status || "pending") === "approved" ? "td-chip-approved" :
                  (position.approval_status || "pending") === "declined" ? "td-chip-declined" : "td-chip-pending"
                }`}>
                  {(position.approval_status || "pending") === "approved" ? <CheckCircle2 size={9} /> :
                   (position.approval_status || "pending") === "declined" ? <XCircle size={9} /> : <Clock size={9} />}
                  {position.approval_status || "pending"}
                </span>
              )}
            </div>
          </div>

          {/* Editable position fields */}
          <div className="td-section-label">Position details</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Symbol *</label>
              <input className="td-form-input" value={form.symbol}
                onChange={(e) => set("symbol", e.target.value.toUpperCase())} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Direction *</label>
              <select className="td-form-select" value={form.trade_type} onChange={(e) => set("trade_type", e.target.value)}>
                <option value="buy">BUY</option>
                <option value="sell">SELL</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Volume (lots) *</label>
              <input className="td-form-input" type="number" step="0.01" value={form.lots}
                onChange={(e) => set("lots", e.target.value)} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Status</label>
              <select className="td-form-select" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>

          {/* Approval status — only meaningful for closed trades */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Approval status</label>
              <select
                className="td-form-select"
                value={form.approval_status}
                onChange={(e) => set("approval_status", e.target.value)}
                disabled={!isClosedForm}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="declined">Declined</option>
              </select>
              {!isClosedForm && (
                <span style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4 }}>
                  Only available for closed trades
                </span>
              )}
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Wallet currency</label>
              <select className="td-form-select" value={walletCurrency} onChange={(e) => setWalletCurrency(e.target.value)}>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Open price *</label>
              <input className="td-form-input" type="number" step="any" value={form.open_price}
                onChange={(e) => set("open_price", e.target.value)} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Current price</label>
              <input className="td-form-input" type="number" step="any" value={form.current_price}
                onChange={(e) => set("current_price", e.target.value)} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Close price</label>
              <input className="td-form-input" type="number" step="any" value={form.close_price}
                onChange={(e) => set("close_price", e.target.value)} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">P&amp;L ($)</label>
              <input className="td-form-input" type="number" step="0.01" value={form.profit_loss}
                onChange={(e) => set("profit_loss", e.target.value)}
                style={{ color: pnlPos ? "var(--green)" : "var(--rose)" }} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Opened at</label>
              <input className="td-form-input" type="datetime-local" value={form.opened_at}
                onChange={(e) => set("opened_at", e.target.value)} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Closed at</label>
              <input className="td-form-input" type="datetime-local" value={form.closed_at}
                onChange={(e) => set("closed_at", e.target.value)} />
            </div>
          </div>

          <div className="td-detail-cell" style={{ background: "var(--bg-elevated)", borderRadius: 8, padding: "8px 10px" }}>
            <div className="td-detail-label">Broker</div>
            <div className="td-detail-val">{position.broker_connections?.broker_name ?? "—"}</div>
          </div>

          {/* Info about settlement + optional delta checkbox when already approved */}
          {isClosedForm && (
            <div className="td-form-row" style={{ background: "var(--accent-dim)", borderRadius: 8, padding: "10px 12px" }}>
              {form.approval_status === "approved" ? (
                <>
                  <div style={{ fontSize: 12, color: "var(--text-primary)", marginBottom: 6 }}>
                    This closed trade is <strong style={{ color: "var(--green)" }}>approved</strong>. Its P&amp;L will be (or already has been) settled to the user's wallet.
                  </div>
                  {pnlChanged && (
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-primary)", cursor: "pointer" }}>
                      <input type="checkbox" checked={adjustWallet} onChange={(e) => setAdjustWallet(e.target.checked)} />
                      Apply the {fmtCurrency(pnlNow - (position.profit_loss ?? 0))} P&amp;L difference to the wallet
                    </label>
                  )}
                </>
              ) : form.approval_status === "declined" ? (
                <div style={{ fontSize: 12, color: "var(--text-primary)" }}>
                  This trade is <strong style={{ color: "var(--rose)" }}>declined</strong>. No balance change will be applied.
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "var(--text-primary)" }}>
                  This closed trade is <strong style={{ color: "var(--amber)" }}>pending approval</strong>.
                  Set approval to <strong>Approved</strong> to credit/debit the P&amp;L ({fmtCurrency(pnlNow)}) to the user's wallet.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="td-modal-foot">
          <button className="td-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="td-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Add Trade Modal ──────────────────────────────────────────────────────────
function AddTradeModal({ onClose, onSaved }) {
  const [users,    setUsers]    = useState([]);
  const [brokers,  setBrokers]  = useState([]);
  const [wallets,  setWallets]  = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState("");
  const [form, setForm] = useState({
    user_id:         "",
    broker_conn_id:  "",
    symbol:          "",
    trade_type:      "buy",
    lots:            "",
    open_price:      "",
    current_price:   "",
    stop_loss:       "",
    take_profit:     "",
    profit_loss:     "0",
    pips:            "0",
    status:          "open",
    approval_status: "pending",
    order_type:      "market",
    currency:        "USD",
  });

  useEffect(() => {
    supabase.from("users").select("id, first_name, last_name, email").order("first_name")
      .then(({ data }) => setUsers(data ?? []));
    supabase.from("broker_connections").select("id, user_id, broker_name").order("broker_name")
      .then(({ data }) => setBrokers(data ?? []));
    supabase.from("wallets").select("id, user_id, currency, balance").order("currency")
      .then(({ data }) => setWallets(data ?? []));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Filter brokers to selected user
  const userBrokers = form.user_id
    ? brokers.filter((b) => b.user_id === form.user_id)
    : brokers;

  // Filter wallets to selected user
  const userWallets = form.user_id
    ? wallets.filter((w) => w.user_id === form.user_id)
    : [];

  // Default currency to the user's first existing wallet once one is selected
  useEffect(() => {
    if (form.user_id && userWallets.length > 0) {
      const stillValid = userWallets.some((w) => w.currency === form.currency);
      if (!stillValid) set("currency", userWallets[0].currency);
    }
  }, [form.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // (applyWalletAdjustment is now a shared helper defined above PositionModal)

  async function handleSave() {
    setErr("");
    if (!form.user_id)    return setErr("Please select a user.");
    if (!form.symbol)     return setErr("Symbol is required.");
    if (!form.lots)       return setErr("Lots / volume is required.");
    if (!form.open_price) return setErr("Open price is required.");
    setSaving(true);
    const profitLoss = parseFloat(form.profit_loss) || 0;
    const isClosed   = form.status === "closed";
    const payload = {
      user_id:          form.user_id,
      broker_conn_id:   form.broker_conn_id || null,
      symbol:           form.symbol.toUpperCase().trim(),
      trade_type:       form.trade_type,
      lots:             parseFloat(form.lots),
      open_price:       parseFloat(form.open_price),
      current_price:    form.current_price ? parseFloat(form.current_price) : parseFloat(form.open_price),
      stop_loss:        form.stop_loss  ? parseFloat(form.stop_loss)  : null,
      take_profit:      form.take_profit ? parseFloat(form.take_profit) : null,
      profit_loss:      profitLoss,
      pips:             parseFloat(form.pips) || 0,
      status:           form.status,
      approval_status:  isClosed ? (form.approval_status || "pending") : "pending",
      order_type:       form.order_type,
    };

    const { error } = await supabase.from("open_positions").insert(payload);
    if (error) {
      setSaving(false);
      return setErr(error.message);
    }

    // Only settle P&L into the wallet when the trade is closed AND approved.
    // Pending / declined closed trades stay off the balance until an admin approves.
    if (isClosed && payload.approval_status === "approved") {
      try {
        await applyWalletAdjustment(form.user_id, form.currency, profitLoss);
      } catch (walletErr) {
        setSaving(false);
        return setErr(`Trade saved, but wallet update failed: ${walletErr.message}`);
      }
    }

    setSaving(false);
    onSaved();
    onClose();
  }

  return (
    <div className="td-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="td-modal" style={{ maxWidth: 560 }}>
        <div className="td-modal-head">
          <div>
            <div className="td-modal-title">Add new trade</div>
            <div className="td-modal-sub">Manually insert a position for a user</div>
          </div>
          <button className="td-close-btn" onClick={onClose}><X size={13} /></button>
        </div>

        <div className="td-modal-body" style={{ gap: 12 }}>
          {err && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "var(--rose-dim)", border: "1px solid rgba(251,113,133,0.25)", fontSize: 12, color: "var(--rose)" }}>
              {err}
            </div>
          )}

          {/* User */}
          <div className="td-form-row">
            <label className="td-form-label">User *</label>
            <select className="td-form-select" value={form.user_id} onChange={(e) => { set("user_id", e.target.value); set("broker_conn_id", ""); }}>
              <option value="">— select user —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
              ))}
            </select>
          </div>

          {/* Broker */}
          <div className="td-form-row">
            <label className="td-form-label">Broker connection</label>
            <select className="td-form-select" value={form.broker_conn_id} onChange={(e) => set("broker_conn_id", e.target.value)}>
              <option value="">— none —</option>
              {userBrokers.map((b) => (
                <option key={b.id} value={b.id}>{b.broker_name}</option>
              ))}
            </select>
          </div>

          {/* Symbol + Trade type */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Symbol *</label>
              <input className="td-form-input" placeholder="e.g. EURUSD" value={form.symbol}
                onChange={(e) => set("symbol", e.target.value.toUpperCase())} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Direction *</label>
              <select className="td-form-select" value={form.trade_type} onChange={(e) => set("trade_type", e.target.value)}>
                <option value="buy">BUY</option>
                <option value="sell">SELL</option>
              </select>
            </div>
          </div>

          {/* Lots + Order type */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Lots *</label>
              <input className="td-form-input" type="number" step="0.01" placeholder="0.10" value={form.lots}
                onChange={(e) => set("lots", e.target.value)} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Order type</label>
              <select className="td-form-select" value={form.order_type} onChange={(e) => set("order_type", e.target.value)}>
                <option value="market">Market</option>
                <option value="limit">Limit</option>
                <option value="stop">Stop</option>
              </select>
            </div>
          </div>

          {/* Open + Current price */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Open price *</label>
              <input className="td-form-input" type="number" step="any" placeholder="1.08500" value={form.open_price}
                onChange={(e) => set("open_price", e.target.value)} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Current price</label>
              <input className="td-form-input" type="number" step="any" placeholder="same as open" value={form.current_price}
                onChange={(e) => set("current_price", e.target.value)} />
            </div>
          </div>

          {/* SL + TP */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Stop loss</label>
              <input className="td-form-input" type="number" step="any" placeholder="optional" value={form.stop_loss}
                onChange={(e) => set("stop_loss", e.target.value)} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Take profit</label>
              <input className="td-form-input" type="number" step="any" placeholder="optional" value={form.take_profit}
                onChange={(e) => set("take_profit", e.target.value)} />
            </div>
          </div>

          {/* P&L + Pips */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">P&amp;L ($)</label>
              <input className="td-form-input" type="number" step="0.01" placeholder="0.00" value={form.profit_loss}
                onChange={(e) => set("profit_loss", e.target.value)} />
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Pips</label>
              <input className="td-form-input" type="number" step="0.01" placeholder="0.00" value={form.pips}
                onChange={(e) => set("pips", e.target.value)} />
            </div>
          </div>

          {/* Wallet currency to adjust */}
          <div className="td-form-row">
            <label className="td-form-label">Wallet to adjust</label>
            <select className="td-form-select" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
              {userWallets.length > 0 ? (
                userWallets.map((w) => (
                  <option key={w.currency} value={w.currency}>
                    {w.currency} (current: {Number(w.balance).toFixed(2)})
                  </option>
                ))
              ) : (
                <option value={form.currency}>{form.currency} (new wallet will be created)</option>
              )}
            </select>
            <span style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, display: "block" }}>
              {form.status === "closed" && form.approval_status === "approved"
                ? "Because this closed trade is approved, the P&L will be settled to the selected wallet immediately."
                : "Closed trades stay off the balance until an admin sets Approval = Approved."}
            </span>
          </div>

          {/* Status + Approval */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="td-form-row">
              <label className="td-form-label">Status</label>
              <select className="td-form-select" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="td-form-row">
              <label className="td-form-label">Approval status</label>
              <select
                className="td-form-select"
                value={form.approval_status}
                onChange={(e) => set("approval_status", e.target.value)}
                disabled={form.status !== "closed"}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="declined">Declined</option>
              </select>
            </div>
          </div>
        </div>

        <div className="td-modal-foot">
          <button className="td-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="td-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Add trade"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Trading() {
  const navigate = useNavigate();

  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminInitials, setAdminInitials] = useState("A");
  const [sessionReady,  setSessionReady]  = useState(false);

  const [tab,       setTab]       = useState("open");
  const [positions, setPositions] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [sortField, setSortField] = useState("opened_at");
  const [sortDir,   setSortDir]   = useState(-1);
  const [page,      setPage]      = useState(1);
  const [viewPos,   setViewPos]   = useState(null);
  const [addTrade,  setAddTrade]  = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  // ── Session gate ────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/admin/login", { replace: true }); return; }
      const email = session.user?.email ?? "";
      setAdminEmail(email);
      const parts = email.split("@")[0].split(".");
      setAdminInitials((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? ""));
      setSessionReady(true);
    });
  }, [navigate]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  // ── Fetch ───────────────────────────────────────────────────────────────
  const fetchPositions = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("open_positions")
      .select(`
        id, symbol, trade_type, lots, open_price, current_price, close_price,
        profit_loss, status, approval_status, opened_at, closed_at,
        users ( id, first_name, last_name, email ),
        broker_connections ( broker_name )
      `)
      .order("opened_at", { ascending: false });
    if (error) console.error("[Trading] fetch error:", error.message);
    setPositions(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { if (sessionReady) fetchPositions(); }, [sessionReady, fetchPositions]);

  // ── Derived ─────────────────────────────────────────────────────────────
  const openPos   = positions.filter((p) => p.status === "open");
  const closedPos = positions.filter((p) => p.status !== "open");
  const source    = tab === "open" ? openPos : closedPos;

  const openPnl  = openPos.reduce((s, p)   => s + (p.profit_loss ?? 0), 0);
  const totalPnl = positions.reduce((s, p) => s + (p.profit_loss ?? 0), 0);
  const winRate  = closedPos.length
    ? Math.round((closedPos.filter((p) => (p.profit_loss ?? 0) >= 0).length / closedPos.length) * 100)
    : 0;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = source.filter((p) => {
      const name = fullName(p.users).toLowerCase();
      return !q || name.includes(q) || p.symbol?.toLowerCase().includes(q) || p.users?.email?.toLowerCase().includes(q);
    });
    list.sort((a, b) => {
      if (sortField === "pnl")    return ((a.profit_loss ?? 0) - (b.profit_loss ?? 0)) * sortDir;
      if (sortField === "symbol") return (a.symbol ?? "").localeCompare(b.symbol ?? "") * sortDir;
      if (sortField === "user")   return fullName(a.users).localeCompare(fullName(b.users)) * sortDir;
      return (new Date(a.opened_at ?? 0) - new Date(b.opened_at ?? 0)) * sortDir;
    });
    return list;
  }, [source, search, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageSlice  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function handleSort(field) {
    if (field === sortField) setSortDir((d) => d * -1);
    else { setSortField(field); setSortDir(-1); }
    setPage(1);
  }

  const STAT_CARDS = [
    { label: "Open trades",   value: openPos.length,       icon: Activity,      bg: "rgba(251,191,36,0.15)",  color: "var(--amber)",        glow: "rgba(251,191,36,0.08)",  bar: "var(--amber)",   sub: "Currently live" },
    { label: "Closed trades", value: closedPos.length,     icon: CandlestickChart, bg: "rgba(99,102,241,0.15)", color: "var(--accent-light)", glow: "rgba(99,102,241,0.08)", bar: "var(--accent)",  sub: "All time" },
    { label: "Floating P&L",  value: fmtCurrency(openPnl), icon: openPnl >= 0 ? TrendingUp : TrendingDown,
      bg: openPnl >= 0 ? "rgba(52,211,153,0.15)" : "rgba(251,113,133,0.15)",
      color: openPnl >= 0 ? "var(--green)" : "var(--rose)",
      glow: openPnl >= 0 ? "rgba(52,211,153,0.08)" : "rgba(251,113,133,0.08)",
      bar: openPnl >= 0 ? "var(--green)" : "var(--rose)", sub: "Open positions" },
    { label: "Win rate",      value: `${winRate}%`,        icon: Briefcase,     bg: "rgba(34,211,238,0.15)",  color: "var(--cyan)",         glow: "rgba(34,211,238,0.08)",  bar: "var(--cyan)",    sub: "Closed trades" },
  ];

  const COLS = [
    { key: "user",      label: "User"    },
    { key: "symbol",    label: "Symbol"  },
    { key: "opened_at", label: "Opened"  },
    { key: "pnl",       label: "P&L"     },
  ];

  if (!sessionReady) {
    return (
      <div className="td-gate">
        <StyleInjector />
        <div className="td-gate-icon"><CandlestickChart size={16} color="#fff" /></div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "Inter, sans-serif" }}>Verifying session…</p>
      </div>
    );
  }

  return (
    <div className="td-shell">
      <StyleInjector />

      {/* ── Sidebar ── */}
      {mobileNavOpen && <div className="ad-sidebar-overlay" onClick={() => setMobileNavOpen(false)} />}
      <aside className={`ad-sidebar${mobileNavOpen ? " open" : ""}`}>
        <div className="ad-logo">
          <div className="ad-logo-icon"><CandlestickChart size={16} color="#fff" /></div>
          <div className="ad-logo-text">
            <strong>OptimStock</strong>
            <span>Admin panel</span>
          </div>
          <button className="ad-sidebar-close" onClick={() => setMobileNavOpen(false)} title="Close menu">
            <X size={15} />
          </button>
        </div>

        <nav className="ad-nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="ad-nav-section-label">{section.label}</p>
              <div className="ad-nav-items">
                {section.items.map((item) => <SidebarLink key={item.to} {...item} onNavigate={() => setMobileNavOpen(false)} />)}
              </div>
            </div>
          ))}
        </nav>

        <div className="ad-user-card">
          <div className="ad-user-inner">
            <div className="ad-user-avatar">{adminInitials.toUpperCase()}</div>
            <div className="ad-user-info">
              <strong>Super Admin</strong>
              <span>{adminEmail}</span>
            </div>
            <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Page ── */}
      <div className="td-page">

        {/* Topbar */}
        <header className="td-topbar">
          <div className="td-topbar-left">
            <button className="td-menu-btn" onClick={() => setMobileNavOpen(true)} title="Open menu">
              <Menu size={16} />
            </button>
            <div className="td-topbar-badge"><CandlestickChart size={9} />Trading</div>
            <div className="td-topbar-titles">
              <div className="td-topbar-title">Trade management</div>
              <div className="td-topbar-sub">View and manage users' open and closed positions</div>
            </div>
          </div>
          <div className="td-topbar-right">
            <button className="td-btn" onClick={fetchPositions}><RefreshCw size={12} />Refresh</button>
            <button className="td-btn td-btn-accent" onClick={() => setAddTrade(true)}><Plus size={12} />Add trade</button>
          </div>
        </header>

        <main className="td-main">

          {/* Stat cards */}
          <div className="td-stat-grid">
            {STAT_CARDS.map(({ label, value, icon: Icon, bg, color, glow, bar, sub }) => (
              <div key={label} className="td-stat" style={{ "--stat-glow": glow, "--stat-bar": bar }}>
                <div className="td-stat-top">
                  <span className="td-stat-label">{label}</span>
                  <div className="td-stat-icon" style={{ background: bg }}>
                    <Icon size={14} style={{ color }} />
                  </div>
                </div>
                <div>
                  <div className="td-stat-value">{value}</div>
                  {sub && <div className="td-stat-sub">{sub}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="td-tabs">
            {[
              { key: "open",   label: "Open trades",   count: openPos.length,   live: openPos.length > 0 },
              { key: "closed", label: "Closed trades",  count: closedPos.length, live: false },
            ].map(({ key, label, count, live }) => (
              <button key={key} className={`td-tab${tab === key ? " active" : ""}`}
                onClick={() => { setTab(key); setPage(1); setSearch(""); }}>
                {label}
                <span className={`td-tab-badge${live ? " live" : ""}`}>{count}</span>
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="td-table-card">
            <div className="td-table-head">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="td-table-title" style={{ textTransform: "capitalize" }}>{tab} positions</span>
                <span className="td-table-count">{filtered.length}</span>
              </div>
              <div className="td-search-wrap">
                <Search size={12} className="td-search-icon" />
                <input type="text" className="td-search" placeholder="Search user or symbol…"
                  value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table className="td-table">
                <thead>
                  <tr>
                    {COLS.map(({ key, label }) => (
                      <th key={key} className="td-th" onClick={() => handleSort(key)}>
                        {label}<SortIcon field={key} sortField={sortField} sortDir={sortDir} />
                      </th>
                    ))}
                    <th className="td-th">Direction</th>
                    <th className="td-th">Volume</th>
                    <th className="td-th">Open price</th>
                    {tab === "closed" && <th className="td-th">Closed at</th>}
                    {tab === "closed" && <th className="td-th">Approval</th>}
                    <th className="td-th right">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i} className="td-tr">
                        {Array.from({ length: tab === "closed" ? 9 : 7 }).map((_, j) => (
                          <td key={j} className="td-td">
                            <div className="td-skel" style={{ height: 12, width: `${40 + Math.random() * 40}%` }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pageSlice.length === 0 ? (
                    <tr>
                      <td colSpan={tab === "closed" ? 9 : 7} className="td-td">
                        <div className="td-empty">
                          <div className="td-empty-icon"><AlertCircle size={16} /></div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>No {tab} trades found</span>
                          {search && <span style={{ fontSize: 11 }}>Try clearing your search filter</span>}
                        </div>
                      </td>
                    </tr>
                  ) : pageSlice.map((p) => {
                    const pnl    = p.profit_loss ?? 0;
                    const pnlPos = pnl >= 0;
                    const aColor = avatarColor(p.users?.id ?? "");
                    return (
                      <tr key={p.id} className="td-tr">
                        {/* User */}
                        <td className="td-td">
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div className={`td-avatar td-avatar-${aColor}`}>
                              {initials(p.users)}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap" }}>{fullName(p.users)}</div>
                              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.users?.email}</div>
                            </div>
                          </div>
                        </td>
                        {/* Symbol */}
                        <td className="td-td">
                          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>{p.symbol ?? "—"}</span>
                        </td>
                        {/* Opened */}
                        <td className="td-td">
                          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{fmtDateTime(p.opened_at)}</span>
                        </td>
                        {/* P&L */}
                        <td className="td-td">
                          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            {pnlPos
                              ? <TrendingUp  size={12} style={{ color: "var(--green)" }} />
                              : <TrendingDown size={12} style={{ color: "var(--rose)"  }} />}
                            <span style={{ fontSize: 13, fontWeight: 700, color: pnlPos ? "var(--green)" : "var(--rose)", fontFamily: "var(--font-mono)" }}>
                              {pnlPos ? "+" : ""}{fmtCurrency(pnl)}
                            </span>
                          </div>
                        </td>
                        {/* Direction */}
                        <td className="td-td">
                          <span className={`td-chip ${p.trade_type === "buy" ? "td-chip-buy" : "td-chip-sell"}`}>
                            {p.trade_type?.toUpperCase() ?? "—"}
                          </span>
                        </td>
                        {/* Volume */}
                        <td className="td-td">
                          <span style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>{p.lots ?? "—"}</span>
                        </td>
                        {/* Open price */}
                        <td className="td-td">
                          <span style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>{p.open_price != null ? fmtCurrency(p.open_price) : "—"}</span>
                        </td>
                        {/* Closed at (closed tab only) */}
                        {tab === "closed" && (
                          <td className="td-td">
                            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{fmtDateTime(p.closed_at)}</span>
                          </td>
                        )}
                        {/* Approval (closed tab only) */}
                        {tab === "closed" && (
                          <td className="td-td">
                            {(() => {
                              const appr = (p.approval_status || "pending").toLowerCase();
                              const chipClass =
                                appr === "approved" ? "td-chip-approved" :
                                appr === "declined" ? "td-chip-declined" : "td-chip-pending";
                              const Icon =
                                appr === "approved" ? CheckCircle2 :
                                appr === "declined" ? XCircle : Clock;
                              return (
                                <span className={`td-chip ${chipClass}`}>
                                  <Icon size={9} />
                                  {appr}
                                </span>
                              );
                            })()}
                          </td>
                        )}
                        {/* Detail btn */}
                        <td className="td-td" style={{ textAlign: "right" }}>
                          <button className="td-icon-btn" onClick={() => setViewPos(p)}>
                            <Eye size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="td-pagination">
                <span className="td-page-info">
                  Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div className="td-page-btns">
                  {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map((p) => (
                    <button key={p} className={`td-page-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

        </main>
      </div>

      {viewPos   && <PositionModal position={viewPos} onClose={() => setViewPos(null)} onSaved={fetchPositions} />}
      {addTrade  && <AddTradeModal onClose={() => setAddTrade(false)} onSaved={fetchPositions} />}
    </div>
  );
}